import { useAtomValue } from 'jotai';
import Quill from 'quill/core';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../../app/redux/useAppSelector';
import EmojiPicker, {
  EmojiPickerPopOver,
} from '../../../components/EmojiPicker';
import type { MenuItemElement } from '../../../components/Menu';
import { MenuItem } from '../../../components/Menu';
import annotationIcon from '../../../icons/non-prefilled/16x16/annotation.svg';
import reactionIcon from '../../../icons/non-prefilled/16x16/reaction.svg';
import { useCreateReaction } from '../../../post/MagneticWall';
import { usePendingMagneticWallItem } from '../../../post/PendingMagneticWallItemProvider';
import useIsLayoutSize from '../../../post/utils/useIsLayoutSize';
import { LayoutSize } from '../../../post/utils/useLayoutSize';
import { lastCodeBlockSyntaxAtom } from '../../../ui/atoms';
import { useQuill, useQuillSelection } from '../../QuillContext';
import convertSelectionToCodeEmbed from '../../code/convertSelectionToCodeEmbed';
import { EditorActions } from '../../editorSlice';
import {
  boldIcon,
  codeIcon,
  italicIcon,
  linkIcon,
  strikethroughIcon,
} from '../editorIcons';
import Toolbar, {
  ToolbarOverflowMenu,
  useToolbarOverflowMenuClose,
} from './Toolbar';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarHighlightButton from './buttons/ToolbarHighlightButton';
import type { ToolbarObjectButtonOptions } from './buttons/ToolbarObjectButton';
import ToolbarObjectButton from './buttons/ToolbarObjectButton';
import ToolbarCommentButton from './buttons/comments/ToolbarCommentButton';
import ToolbarReactionButton from './buttons/comments/ToolbarReactionButton';

interface ReactionMenuItemProps {
  overflowMenuButtonRef: React.RefObject<HTMLDivElement | null>;
  overflowMenuItemsRef: React.RefObject<HTMLDivElement | null>;
}

const ReactionMenuItem = ({
  overflowMenuButtonRef,
  overflowMenuItemsRef,
}: ReactionMenuItemProps) => {
  const closeOverflowMenu = useToolbarOverflowMenuClose();
  const createReaction = useCreateReaction();
  const dispatch = useAppDispatch();
  const isSmall = useIsLayoutSize(LayoutSize.mobile);
  const menuItemRef = useRef<MenuItemElement>(null);

  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [bounds, setBounds] = useState({ top: 0, bottom: 0 });

  const handleOpen = (open: boolean) => {
    setIsEmojiPickerOpen(open);
    dispatch(EditorActions.setIsPickingReaction(open));
  };

  useLayoutEffect(() => {
    // Accessing the menu parent to include padding in calculation
    const overflowMenuItemsRect =
      overflowMenuItemsRef.current?.parentElement?.getBoundingClientRect();
    const menuItemRect = menuItemRef.current?.getBoundingClientRect();
    const overflowMenuRect =
      overflowMenuButtonRef.current?.getBoundingClientRect();
    if (!overflowMenuItemsRect || !menuItemRect || !overflowMenuRect) {
      return;
    }

    setBounds({
      top: -(menuItemRect.top - overflowMenuRect.top + 1), // 1 px bor border compensation
      bottom: -(overflowMenuItemsRect.bottom - menuItemRect.bottom + 1),
    });
  }, [overflowMenuItemsRef, overflowMenuButtonRef, isEmojiPickerOpen]);

  return (
    <EmojiPickerPopOver
      bounds={bounds}
      placement={isSmall ? 'bottom-end' : 'right-start'}
      open={isEmojiPickerOpen}
      setOpen={handleOpen}
      target={
        <MenuItem
          ref={menuItemRef}
          icon={reactionIcon}
          onClick={() => handleOpen(!isEmojiPickerOpen)}
        >
          Add reaction
        </MenuItem>
      }
    >
      <EmojiPicker
        onPick={(picked: string) => {
          createReaction(picked);
          handleOpen(false);
          closeOverflowMenu();
        }}
      />
    </EmojiPickerPopOver>
  );
};

interface AnnotationMenuItemProps {
  onClick: () => void;
}

const AnnotationMenuItem = ({ onClick }: AnnotationMenuItemProps) => {
  const { createPendingAnnotation } = usePendingMagneticWallItem();
  return (
    <MenuItem
      icon={annotationIcon}
      onClick={() => {
        onClick();
        createPendingAnnotation();
      }}
    >
      Add annotation
    </MenuItem>
  );
};

interface EditModeToolbarProps {
  multiline: boolean;
  inTitleLine: boolean;
  closeToolbar: () => void;
  onLinkSelected: () => void;
  objectButtonOptions?: ToolbarObjectButtonOptions;
}

const EditModeToolbar = ({
  multiline,
  inTitleLine,
  closeToolbar,
  onLinkSelected,
  objectButtonOptions,
}: EditModeToolbarProps) => {
  const { quill } = useQuill();
  const { formats } = useQuillSelection();
  const overflowMenuButtonRef = useRef<HTMLDivElement>(null);
  const overflowMenuItemsRef = useRef<HTMLDivElement>(null);

  const isSupportingCodeEmbed = useMemo(
    () => !!quill?.scroll.query('code-embed'),
    [quill],
  );
  const isSupportingInlineCode = useMemo(
    () => !!quill?.scroll.query('code'),
    [quill],
  );
  const isSupportingAnnotations = useMemo(
    () => !!quill?.scroll.query('annotation'),
    [quill],
  );
  const isSupportingComments = useMemo(
    () => !!quill?.scroll.query('comment'),
    [quill],
  );

  const lastCodeBlockSyntax = useAtomValue(lastCodeBlockSyntaxAtom);

  const handleFormat = (format: string, value: unknown) =>
    quill?.format(format, value, Quill.sources.USER);
  const toggleFormat = (name: string) => () =>
    handleFormat(name, !formats[name]);

  const existingComment = Array.isArray(formats.comment)
    ? (formats.comment as string[])
    : null;

  return (
    <Toolbar
      overflowMenu={
        isSupportingAnnotations ? (
          <ToolbarOverflowMenu
            ref={overflowMenuButtonRef}
            rightAlignOverflowMenu
          >
            <div ref={overflowMenuItemsRef}>
              {isSupportingComments && (
                <ReactionMenuItem
                  overflowMenuButtonRef={overflowMenuButtonRef}
                  overflowMenuItemsRef={overflowMenuItemsRef}
                />
              )}
              <AnnotationMenuItem onClick={closeToolbar} />
            </div>
          </ToolbarOverflowMenu>
        ) : undefined
      }
    >
      {!formats.table && (
        <ToolbarButtonGroup>
          <ToolbarObjectButton
            disabled={inTitleLine}
            options={objectButtonOptions}
          />
        </ToolbarButtonGroup>
      )}
      <ToolbarButtonGroup>
        <ToolbarButton
          accessibleType="toggle"
          icon={boldIcon}
          active={!!formats.bold}
          disabled={inTitleLine || !!formats.header}
          tooltip="Bold"
          shortcut="Ctrl + b"
          onClick={toggleFormat('bold')}
        />
        <ToolbarButton
          accessibleType="toggle"
          icon={italicIcon}
          active={!!formats.italic}
          disabled={inTitleLine}
          tooltip="Italic"
          shortcut="Ctrl + i"
          onClick={toggleFormat('italic')}
        />
        <ToolbarButton
          accessibleType="toggle"
          icon={strikethroughIcon}
          active={!!formats.strike}
          disabled={inTitleLine}
          tooltip="Strikethrough"
          shortcut="Ctrl + Alt + x"
          onClick={toggleFormat('strike')}
        />
        {isSupportingInlineCode && (
          <ToolbarButton
            icon={codeIcon}
            active={!!formats.code}
            disabled={inTitleLine || (multiline && !isSupportingCodeEmbed)}
            tooltip="Inline code"
            onClick={() => {
              if (!quill) return;

              if (!multiline) {
                handleFormat('code', !formats.code);
                return;
              }

              if (isSupportingCodeEmbed) {
                const range = quill.getSelection(true);
                convertSelectionToCodeEmbed(
                  quill,
                  range.index,
                  range.length,
                  lastCodeBlockSyntax,
                );
              }
            }}
          />
        )}
        <ToolbarButton
          icon={linkIcon}
          disabled={inTitleLine || multiline}
          active={!!formats.link}
          tooltip="Link"
          shortcut="Ctrl + k"
          onClick={onLinkSelected}
        />
      </ToolbarButtonGroup>
      {quill && (
        <ToolbarButtonGroup>
          <ToolbarHighlightButton
            quill={quill}
            value={formats.highlight}
            disabled={inTitleLine}
            onChange={(value) => handleFormat('highlight', value)}
          />
        </ToolbarButtonGroup>
      )}
      {isSupportingComments && (
        <ToolbarButtonGroup>
          {!isSupportingAnnotations && <ToolbarReactionButton />}
          <ToolbarCommentButton
            existingComment={existingComment}
            onClick={closeToolbar}
          />
        </ToolbarButtonGroup>
      )}
    </Toolbar>
  );
};

export default EditModeToolbar;
